import { Product } from '@components/ui/Product';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { CheckCircle2, Clock, Package, ShoppingCart, User, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ReceivedLine, REJECTION_REASONS } from '../../models';

interface ReceivedLinesListProps {
    lines: ReceivedLine[];
    isLoading?: boolean;
}

// Función auxiliar para construir URL completa de imagen
const buildImageUrl = (imageHash?: string) => {
    if (!imageHash) return '';
    if (imageHash.startsWith('http')) return imageHash;
    return `https://warehouse-images-stg-dev.nyc3.digitaloceanspaces.com/${imageHash}`;
};

// Función auxiliar para formatear fecha
const formatDate = (dateString?: string | null) => {
    // Validar que dateString existe
    if (!dateString) {
        return 'Fecha no disponible';
    }

    // Convertir formato "2025-01-23 19:09:30" a formato ISO "2025-01-23T19:09:30"
    const isoDateString = dateString.replace(' ', 'T');
    const date = new Date(isoDateString);

    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
        return dateString; // Retornar el string original si no se puede parsear
    }

    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Hace un momento';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Hace ${diffInHours}h`;

    // Formato: DD/MM/YYYY HH:MM
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const ReceivedLinesList: React.FC<ReceivedLinesListProps> = ({
    lines,
    isLoading = false,
}) => {
    // Estado para forzar re-render cada minuto y actualizar las fechas relativas
    const [, setCurrentTime] = useState(new Date());

    useEffect(() => {
        // Actualizar cada minuto para refrescar los textos de "Hace X min"
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000); // 60 segundos

        return () => clearInterval(interval);
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">Cargando productos recibidos...</p>
                </div>
            </div>
        );
    }

    if (lines.length === 0) {
        return (
            <div className="flex items-center justify-center h-full bg-muted/30 rounded-xl border border-dashed border-border">
                <div className="text-center p-8">
                    <Package className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                        No hay productos recibidos
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Los productos recepcionados aparecerán aquí
                    </p>
                </div>
            </div>
        );
    }

    // Ordenar por fecha de creación (más recientes primero)
    const sortedLines = [...lines].sort((a, b) => {
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        const dateA = new Date(a.created_at.replace(' ', 'T'));
        const dateB = new Date(b.created_at.replace(' ', 'T'));
        return dateB.getTime() - dateA.getTime();
    });

    return (
        <div className="h-full flex flex-col">
            {/* Lista ordenada cronológicamente */}
            <ScrollArea className="flex-1">
                <div className="space-y-3 pr-4">
                    {sortedLines.map((line) => {
                        const rejectionLabel = line.rejection_reason
                            ? REJECTION_REASONS.find(r => r.value === line.rejection_reason)?.label || line.rejection_reason
                            : null;
                        const isAccepted = line.status === 'ACEPTADO';

                        return (
                            <div
                                key={line.id}
                                className={`border rounded-lg p-3 transition-colors ${isAccepted
                                    ? 'bg-background border-border hover:border-green-300 dark:hover:border-green-700'
                                    : 'bg-background border-border hover:border-red-300 dark:hover:border-red-700'
                                    }`}
                            >
                                {/* Producto */}
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="flex-1">
                                        <Product
                                            id={line.product_id}
                                            src={buildImageUrl(line.product?.image || line.product?.image_hash)}
                                            name={line.product?.name || `Producto ID: ${line.product_id}`}
                                            code={line.product?.code || ''}
                                            acronym={line.product?.name?.charAt(0).toUpperCase()}
                                            showImageModal={true}
                                            enableLineClamp={true}
                                        />
                                    </div>

                                    {/* Estado y cantidad */}
                                    <div className="flex flex-col items-end gap-1">
                                        {isAccepted ? (
                                            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        ) : (
                                            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        )}
                                        <span className="text-sm font-semibold text-foreground">
                                            {line.quantity} ud.
                                        </span>
                                    </div>
                                </div>

                                {/* Info adicional */}
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(line.created_at)}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Package className="w-3 h-3" />
                                            {line.order?.ref || `Pedido #${line.order_id}`}
                                        </span>
                                        {line.cart?.code && (
                                            <span className="flex items-center gap-1">
                                                <ShoppingCart className="w-3 h-3" />
                                                {line.cart.code}
                                            </span>
                                        )}
                                        {line.returned_reception_order?.processed_by_user && (
                                            <span className="flex items-center gap-1">
                                                <User className="w-3 h-3" />
                                                {line.returned_reception_order.processed_by_user.name}
                                            </span>
                                        )}
                                    </div>
                                    {rejectionLabel && (
                                        <span className="text-red-600 dark:text-red-400 font-medium">
                                            {rejectionLabel}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </ScrollArea>
        </div>
    );
};

